(() => {
    const BREAKPOINT_LG = 992;
    const TRANSITION_MS = 600;

    function initSlider(section) {
        const stage = section.querySelector('.c-people-slider__stage');
        const track = section.querySelector('.c-people-slider__track');
        if (!track || !stage) return;

        const realSlides = Array.from(track.querySelectorAll('.c-people-slider__slide'));
        if (!realSlides.length) return;

        const n = realSlides.length;
        const panel = section.querySelector('.c-people-slider__panel');
        const panelQuote = section.querySelector('.c-people-slider__panel-quote');
        const btnsPrev = section.querySelectorAll('.c-people-slider__btn[data-dir="-1"]');
        const btnsNext = section.querySelectorAll('.c-people-slider__btn[data-dir="1"]');

        // Clone first and last for seamless infinite loop.
        // Track order: [clone_last] [slide_0 … slide_n-1] [clone_first]
        // Visual indices: 0 = clone_last, 1…n = real slides, n+1 = clone_first
        const lastClone = realSlides[n - 1].cloneNode(true);
        const firstClone = realSlides[0].cloneNode(true);
        track.insertBefore(lastClone, realSlides[0]);
        track.appendChild(firstClone);

        [lastClone, firstClone].forEach(clone => {
            clone.querySelectorAll('img').forEach(img => { img.loading = 'eager'; });
        });

        let realIndex = 0;
        let vIndex = 1;
        let animating = false;

        const isDesktop = () => window.innerWidth >= BREAKPOINT_LG;

        function getSlideW() {
            const first = track.querySelector('.c-people-slider__slide');
            return first ? first.offsetWidth : 0;
        }

        function getDesktopPanelW() {
            return parseFloat(getComputedStyle(section).getPropertyValue('--ps-panel-w')) || 300;
        }

        // Reads --ps-gap from CSS so mobile (8px) and desktop (16px) are in sync.
        function getGap() {
            return parseFloat(getComputedStyle(section).getPropertyValue('--ps-gap')) || 16;
        }

        function getPeekLeft(slideW, gap) {
            if (isDesktop()) {
                // peekLeft = (stageW - slideW - gap - panelW) / 2 — symmetric peeks
                const panelW = getDesktopPanelW();
                return Math.max(0, (stage.offsetWidth - slideW - gap - panelW) / 2);
            }
            // Mobile: equal left/right peek — peekLeft = (stageW - slideW) / 2
            // visible peek = peekLeft - gap  (≈10px at 390px viewport)
            return Math.max(0, (stage.offsetWidth - slideW) / 2);
        }

        function setVPosition(vi, animated) {
            const slideW = getSlideW();
            const gap = getGap();
            const peekL = getPeekLeft(slideW, gap);
            const offset = peekL - vi * (slideW + gap);

            track.style.transition = animated
                ? `transform ${TRANSITION_MS}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`
                : 'none';
            if (!animated) {
                void track.offsetWidth; // force reflow so instant jump isn't swallowed
            }
            track.style.transform = `translateX(${offset}px)`;
        }

        function getQuoteText(ri) {
            const el = realSlides[ri] && realSlides[ri].querySelector('.c-people-slider__slide-quote');
            return el ? el.textContent.trim() : '';
        }

        function fadeQuote(text) {
            if (!panelQuote) return;
            panelQuote.style.opacity = '0';
            setTimeout(() => {
                panelQuote.textContent = text;
                panelQuote.style.opacity = '1';
            }, 200);
        }

        // Toggle .is-active on real slides + keep clones in sync so the
        // wrap-around frame looks correct during the instant snap.
        function updateActive(prevReal, nextReal) {
            realSlides[prevReal].classList.remove('is-active');
            realSlides[nextReal].classList.add('is-active');
            lastClone.classList.toggle('is-active', nextReal === n - 1);
            firstClone.classList.toggle('is-active', nextReal === 0);
        }

        function goTo(targetVI) {
            if (animating) return;
            animating = true;

            let targetReal;
            if (targetVI <= 0) {
                targetReal = n - 1;       // clone of last → real last
            } else if (targetVI >= n + 1) {
                targetReal = 0;           // clone of first → real first
            } else {
                targetReal = targetVI - 1;
            }

            updateActive(realIndex, targetReal);
            stage.classList.add('is-animating');
            setVPosition(targetVI, true);

            if (isDesktop()) fadeQuote(getQuoteText(targetReal));

            setTimeout(() => {
                if (targetVI <= 0 || targetVI >= n + 1) {
                    vIndex = targetReal + 1;
                    setVPosition(vIndex, false);
                } else {
                    vIndex = targetVI;
                }
                realIndex = targetReal;
                stage.classList.remove('is-animating');
                animating = false;
            }, TRANSITION_MS);
        }

        btnsPrev.forEach(btn => btn.addEventListener('click', () => goTo(vIndex - 1)));
        btnsNext.forEach(btn => btn.addEventListener('click', () => goTo(vIndex + 1)));

        // Touch / swipe
        let touchStartX = 0, touchStartY = 0, touchIntent = null;
        track.addEventListener('touchstart', e => {
            touchStartX = e.changedTouches[0].screenX;
            touchStartY = e.changedTouches[0].screenY;
            touchIntent = null;
        }, { passive: true });
        track.addEventListener('touchmove', e => {
            if (touchIntent) return;
            const dx = Math.abs(e.changedTouches[0].screenX - touchStartX);
            const dy = Math.abs(e.changedTouches[0].screenY - touchStartY);
            if (dx > 10 || dy > 10) touchIntent = dx > dy ? 'h' : 'v';
        }, { passive: true });
        track.addEventListener('touchend', e => {
            if (touchIntent !== 'h') return;
            const dx = e.changedTouches[0].screenX - touchStartX;
            if (Math.abs(dx) > 40) goTo(vIndex + (dx < 0 ? 1 : -1));
        }, { passive: true });

        let resizeTimer;
        window.addEventListener('resize', () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => {
                setVPosition(vIndex, false);
                if (isDesktop() && panelQuote) {
                    panelQuote.style.transition = 'none';
                    panelQuote.style.opacity = '1';
                    panelQuote.textContent = getQuoteText(realIndex);
                }
            }, 150);
        });

        // ── Initial state ─────────────────────────────────────────────────
        // Set active class immediately so CSS can apply the correct heights
        // and blur states before the first paint (prevents FOUC).
        section.classList.add('js-init');
        realSlides[0].classList.add('is-active');
        // Clones start without is-active (they'll be synced on navigation).
        lastClone.classList.remove('is-active');
        firstClone.classList.remove('is-active');

        // Pre-populate desktop panel quote (non-empty guard preserves any
        // text the block editor already saved into the panel-quote element).
        if (isDesktop() && panelQuote) {
            const q = getQuoteText(0);
            if (q) panelQuote.textContent = q;
        }

        function initPosition() {
            setVPosition(1, false);
            // Enable height/filter/opacity transitions one frame later so the
            // initial state is painted without animation.
            requestAnimationFrame(() => section.classList.add('js-ready'));
        }

        // If layout is already computed (CSS is render-blocking so this is
        // the common case), set position synchronously to avoid any flash.
        if (stage.offsetWidth > 0) {
            initPosition();
        } else {
            requestAnimationFrame(initPosition);
        }
    }

    function init() {
        document.querySelectorAll('.c-people-slider').forEach(initSlider);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
